import { HttpClient, HttpErrorResponse, HttpResponse, HttpParams } from "@angular/common/http";
import { Injectable, inject, Injector, signal } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { BehaviorSubject, Subject, Observable, catchError, finalize, map, of, shareReplay, tap, throwError } from "rxjs";
import { environment } from '../../environments/environment';
import { UserModel, GeneralSettingsModel, JwTokenModel, Permissions, PatientModel } from "../core/api/models";
import { AccountApiService, StaffApiService } from '../core/api/services';
import { PatientSessionService } from "../staff/home/patient-session.service";
import { AlertService } from '../shared/services/alert.service';
import { Utils } from '../core/utils';
import { PatientDataFacadeService } from '../core/services/patient-data-facade.service';
import { FrontendDiagnosticsService } from '../core/services/frontend-diagnostics.service';

export class RefreshSupersededError extends Error {
  constructor() {
    super('The session changed before the token refresh finished.');
    this.name = 'RefreshSupersededError';
  }
}

export function isRefreshSuperseded(error: unknown): boolean {
  return error instanceof RefreshSupersededError;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {

  accountApiService = inject(AccountApiService);

  public tokenInfo$: Subject<JwTokenModel | null> = new Subject<JwTokenModel | null>();
  public tokenInfo: JwTokenModel | null = null;


  public userInfo$: Subject<UserModel | null> = new Subject<UserModel | null>();
  public userInfo: UserModel | null = null;

  public myPatients$: Subject<PatientModel[] | null> = new Subject<PatientModel[] | null>();
  public myPatients: PatientModel[] | null = null;

  public generalSettings$: Subject<GeneralSettingsModel | null> = new Subject<GeneralSettingsModel | null>();
  public generalSettings: GeneralSettingsModel | null = null;

  private _authStateChanged: Subject<boolean> = new BehaviorSubject<boolean>(false);
  private _sessionConfirmVisible = signal<boolean>(false);
  private refreshTokenRequest$: Observable<JwTokenModel | null> | null = null;
  private refreshingCredential: string | null = null;
  private readonly refreshLockKey = 'refreshingToken';
  private readonly refreshOwner = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  private clockCalibrationNotBefore = 0;
  private serverTimeUtcMs: number | null = null;
  private serverTimeObservedAt = 0;

  public observeServerTime(value: string | null): void {
    const time = value ? Date.parse(value) : NaN;
    if (Number.isFinite(time)) {
      const observedAt = performance.now();
      const previousEstimate = this.serverTimeUtcMs === null ? time
        : this.serverTimeUtcMs + observedAt - this.serverTimeObservedAt;
      // Responses may arrive out of order; elapsed server time must not rewind.
      this.serverTimeUtcMs = Math.max(time, previousEstimate);
      this.serverTimeObservedAt = observedAt;
    }
  }

  public accessTokenRemainingMs(): number | null {
    if (this.serverTimeUtcMs === null || !this.tokenInfo?.expires_at) {
      return null;
    }
    const expires = Date.parse(this.tokenInfo.expires_at);
    return Number.isFinite(expires)
      ? expires - (this.serverTimeUtcMs + performance.now() - this.serverTimeObservedAt)
      : null;
  }

  public calibrateServerClock(): void {
    if (this.serverTimeUtcMs !== null || !this.isSignedIn() || performance.now() < this.clockCalibrationNotBefore) {
      return;
    }
    // One uncached probe per half minute. Account/Test is anonymous and its failure must not clear the session.
    this.clockCalibrationNotBefore = performance.now() + 30000;
    this.accountApiService.Test().subscribe({
      next: () => {
        if (this.serverTimeUtcMs === null) {
          this.clockCalibrationNotBefore = performance.now();
        }
      },
      error: () => {
        // Offline or unreachable. Keep the restored session and retry from a later response.
      },
    });
  }

  // Use private property for lazy injection
  private patientSessionServiceInstance: PatientSessionService | null = null;
  private patientDataFacadeServiceInstance: PatientDataFacadeService | null = null;

  // Create getter for PatientSessionService to avoid circular dependency
  private get patientSessionService(): PatientSessionService {
    if (!this.patientSessionServiceInstance) {
      this.patientSessionServiceInstance = this.injector.get(PatientSessionService);
    }
    return this.patientSessionServiceInstance;
  }

  private get patientDataFacadeService(): PatientDataFacadeService {
    if (!this.patientDataFacadeServiceInstance) {
      this.patientDataFacadeServiceInstance = this.injector.get(PatientDataFacadeService);
    }
    return this.patientDataFacadeServiceInstance;
  }

  public onStateChanged() {
    return this._authStateChanged.asObservable();
  }

  constructor(
    private http: HttpClient,
    private router: Router,
    private route: ActivatedRoute,
    private injector: Injector,
    private staffApiService: StaffApiService,
    private alertService: AlertService,
    private diagnostics: FrontendDiagnosticsService,
  ) {

    const tokenInfoData = localStorage.getItem('tokenInfo');
    if (tokenInfoData) {
      this.tokenInfo = JSON.parse(tokenInfoData) as JwTokenModel;
      console.info('Has Token Info');
      this.tokenInfo$.next(this.tokenInfo);
    } else {
      this.tokenInfo$.next(null);
    }

    const currentUserData = localStorage.getItem('userInfo');
    if (currentUserData) {
      this.userInfo = JSON.parse(currentUserData) as UserModel;
      console.info('Has User Info');
      this.userInfo$.next(this.userInfo);
    } else {
      this.userInfo$.next(null);
    }

    const currentMyPatientsData = localStorage.getItem('myPatients');
    if (currentMyPatientsData) {
      this.myPatients = JSON.parse(currentMyPatientsData) as PatientModel[];
      console.info('Has My Patients');
      this.myPatients$.next(this.myPatients);
    }
    const generalSettingsData = localStorage.getItem('generalSettings');
    if (generalSettingsData) {
      this.generalSettings = JSON.parse(generalSettingsData) as GeneralSettingsModel;
      console.info('Has General Settings');
      this.generalSettings$.next(this.generalSettings);
    } else {
      this.generalSettings$.next(null);
    }

    this.tokenInfo$.subscribe(x => {
      console.debug('Token state changed', { hasAccessToken: !!x?.access_token });
      if (x) {
        localStorage.setItem('tokenInfo', JSON.stringify(x));
      }
      else
        localStorage.removeItem('tokenInfo');
      this.tokenInfo = x;
    });

    this.userInfo$.subscribe(x => {
      console.debug('userInfo$', x);
      if (x) {
        localStorage.setItem('userInfo', JSON.stringify(x));
      }
      else
        localStorage.removeItem('userInfo');
      this.userInfo = x;
    });

    this.myPatients$.subscribe(x => {
      console.debug('myPatients$', x);
      if (x) {
        localStorage.setItem('myPatients', JSON.stringify(x));
      }
      else
        localStorage.removeItem('myPatients');
      this.myPatients = x;
    });

    this.generalSettings$.subscribe(x => {
      console.debug('generalSettings$', x);
      if (x) {
        localStorage.setItem('generalSettings', JSON.stringify(x));
      }
      else
        localStorage.removeItem('generalSettings');
      this.generalSettings = x;
    });
  }


  public signIn(email: string, password: string) {


    return this.accountApiService.Authenticate({ body: { username: email, password: password } })
      .pipe(tap(token => {
        if (token && token.access_token) {
          this.tokenInfo$.next(token);
          this._authStateChanged.next(true);
        } else {
          this.tokenInfo$.next(null);
          this._authStateChanged.next(false);
        }
        return;
      },
        error => {
          console.log(error);
          //this.messageService.add({ severity: 'error', detail: 'Internal Error: ' + (error.message || error) });
        }));
  }


  // sign out
  public signOut() {
    return this.signOutNoRedirect().pipe(
      tap((success) => {
        this.router.navigate(['/auth/signin']);
      },
        error => {          
        this.router.navigate(['/auth/signin']);
        })
    );
  }

  public signOutNoRedirect() {
    this.diagnostics.recordEntry('signed-out', {
      HasAccessToken: this.isSignedIn(),
      AccessExpiresAt: this.tokenInfo?.expires_at,
      RefreshExpiresAt: this.tokenInfo?.refresh_expires_at,
      AccessRemainingMs: this.accessTokenRemainingMs(),
    });

    this._authStateChanged.next(false);
    const currenttokenInfo = JSON.parse(localStorage.getItem('tokenInfo') || '{}');
    this.clearOwnRefreshLock(this.tokenInfo?.refresh_token);

    if (this.isSignedIn()) {
      this.accountApiService.Logout()
        .forEach(() => { })
        .catch(err => { })
        .finally(() => { });
    }

    this.tokenInfo$.next(null);
    this.userInfo$.next(null);
    this.myPatients$.next(null);
    this.patientDataFacadeService.clearAll();
    this._authStateChanged.next(false);

    return of(true);

  }



  public isSignedIn(): boolean {
    // This is UI state, not authorization. Like Optomiser, the API validates
    // the token and its expiry using server time, including after a reload.
    return !!this.tokenInfo?.access_token;
  }

  public hasUsableRefreshToken(): boolean {
    // Only the server can decide whether a refresh credential is still valid.
    return !!this.tokenInfo?.refresh_token;
  }


  public redirectAfterLogin(userInfo?: UserModel | null, returnUrl?: string | undefined | null) {
    // replaceUrl drops /auth/signin?token= from history so WKWebView reloads
    // (and WebContent process recovery) do not reuse a spent one-time handoff token.
    const navExtras = { replaceUrl: true };
    userInfo = userInfo || this.userInfo;
    if (userInfo == null || userInfo == undefined)
      this.router.navigate(['/auth/signin'], navExtras);
    else if (returnUrl && returnUrl != '' && returnUrl != '/' && returnUrl != '/auth/signin') {
      const safeReturnUrl = this.getSafeInternalReturnUrl(returnUrl);
      if (!safeReturnUrl) {
        this.alertService.error(
          'Spark could not open the requested page because the destination was invalid.'
        );
        this.router.navigate(['/select-patient'], navExtras);
        return;
      }
      void this.router.navigateByUrl(safeReturnUrl, navExtras);
    }
    //TODO: remove this and change to redirect to the home page
    else {
      //check if there is any patient on the patientSession 
      if (this.patientSessionService.getPatientID()) {
        if (userInfo.Permissions != null && userInfo.Permissions.indexOf(Permissions.SparkApp_Supervisor) >= 0) {
          this.router.navigate(['/supervisor/' + this.patientSessionService.getPatientID() + '/overview'], navExtras);
        }
        else {
          this.router.navigate(['/staff/' + this.patientSessionService.getPatientID() + '/sessions'], navExtras);
        }
      }
      else {
        this.router.navigate(['/select-patient'], navExtras);
      }
    }

  }

  private getSafeInternalReturnUrl(returnUrl: string): string | null {
    const trimmed = returnUrl.trim();
    if (!trimmed || trimmed.toLowerCase().includes('://')) {
      return null;
    }

    const withLeadingSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    const normalized = withLeadingSlash.replace(/^\/+/, '/');
    if (!normalized || normalized === '/' || normalized === '/auth/signin') {
      return null;
    }

    return normalized;
  }

  public hasPermission(permission: Permissions): boolean {
    return this.userInfo?.Permissions?.includes(permission) ?? false;
  }

  public canEditBip(): boolean {
    return this.hasPermission(Permissions.SparkApp_EditBIP);
  }

  public hideSessionConfirm() {
    this._sessionConfirmVisible.set(false);
    this.alertService.clear();
  }

  public showSessionConfirm() {
    if (this._sessionConfirmVisible()) {
      return;
    }
    this.alertService.confirm("Session Expiring", "You have been inactive for 25 minutes. Would you like to continue? If no response is given you will be logged out.").subscribe(x => {
      if (x) {
        
        if (this.hasUsableRefreshToken()) {

          this.refreshTokenOnce().subscribe({ error: () => { } });
        }
        this._sessionConfirmVisible.set(false);
      }
      else {        
        this.signOut().subscribe();
        this._sessionConfirmVisible.set(false);
      }
    });

    Utils.beep();
    this._sessionConfirmVisible.set(true);    
  }


  private _gettingTokenInfo = false;
  public getTokenInfo(): Observable<JwTokenModel | null> {
    const refreshToken = this.tokenInfo?.refresh_token;
    if (!refreshToken) {
      return of(null);
    }
    if (this.refreshTokenRequest$ && this.refreshingCredential === refreshToken) {
      return this.refreshTokenRequest$;
    }
    const shared = this.rotationObservable(refreshToken);
    if (shared) {
      return shared;
    }
    if (this.otherTabOwnsRefreshLock(refreshToken)) {
      return this.waitForSharedRefresh(refreshToken);
    }
    return this.beginRefresh(refreshToken);
  }

  public refreshTokenOnce(): Observable<JwTokenModel | null> {
    return this.getTokenInfo().pipe(catchError((error) =>
      isRefreshSuperseded(error) ? throwError(() => error) : of(null)));
  }

  private beginRefresh(refreshToken: string): Observable<JwTokenModel | null> {
    if (this.refreshTokenRequest$ && this.refreshingCredential === refreshToken) {
      return this.refreshTokenRequest$;
    }
    const shared = this.rotationObservable(refreshToken);
    if (shared) {
      return shared;
    }
    if (this.otherTabOwnsRefreshLock(refreshToken)) {
      return this.waitForSharedRefresh(refreshToken);
    }
    this.writeRefreshLock(refreshToken);
    if (this.otherTabOwnsRefreshLock(refreshToken)) {
      return this.waitForSharedRefresh(refreshToken);
    }

    this._gettingTokenInfo = true;
    this.refreshingCredential = refreshToken;
    const request$: Observable<JwTokenModel | null> = this.accountApiService.RefreshToken({
      body: { refresh_token: refreshToken },
    }).pipe(
      map((result) => {
        const outcome = this.rotationOutcome(refreshToken);
        if (outcome.kind === 'superseded') {
          throw new RefreshSupersededError();
        }
        if (outcome.kind === 'use') {
          return outcome.token;
        }
        if (this.tokenInfo?.refresh_token !== refreshToken) {
          throw new RefreshSupersededError();
        }
        this.tokenInfo$.next(result);
        return result;
      }),
      catchError((error) => {
        if (isRefreshSuperseded(error)) {
          return throwError(() => error);
        }
        this.diagnostics.recordEntry('token-refresh-failed', {
          Status: error.status,
          AccessExpiresAt: this.tokenInfo?.expires_at,
          AccessRemainingMs: this.accessTokenRemainingMs(),
        });
        return this.finishRejectedRefresh(refreshToken, error);
      }),
      finalize(() => {
        if (this.refreshTokenRequest$ === request$) {
          this.refreshTokenRequest$ = null;
          this.refreshingCredential = null;
          this._gettingTokenInfo = false;
        }
        this.clearOwnRefreshLock(refreshToken);
      }),
      shareReplay(1)
    );
    this.refreshTokenRequest$ = request$;
    return request$;
  }

  private finishRejectedRefresh(refreshToken: string, error: unknown): Observable<JwTokenModel | null> {
    const shared = this.rotationObservable(refreshToken);
    if (shared) {
      return shared;
    }
    // The winning tab may still be writing the rotated credential.
    return new Observable(subscriber => {
      let settled = false;
      const stop = () => {
        settled = true;
        window.removeEventListener('storage', onStorage);
        clearTimeout(timerId);
      };
      const deliver = (result: Observable<JwTokenModel | null>) => {
        if (settled) {
          return;
        }
        stop();
        result.subscribe({
          next: value => subscriber.next(value),
          error: err => subscriber.error(err),
          complete: () => subscriber.complete(),
        });
      };
      const look = () => {
        const later = this.rotationObservable(refreshToken);
        if (later) {
          deliver(later);
          return;
        }
        if (this.tokenInfo?.refresh_token === refreshToken) {
          this.userInfo$.next(null);
          this.myPatients$.next(null);
          this.tokenInfo$.next(null);
        }
        deliver(throwError(() => error));
      };
      const onStorage = (event: StorageEvent) => {
        if (event.key !== 'tokenInfo') {
          return;
        }
        const later = this.rotationObservable(refreshToken);
        if (later) {
          deliver(later);
        }
      };
      window.addEventListener('storage', onStorage);
      const timerId = setTimeout(look, 400);
      return () => {
        if (!settled) {
          stop();
        }
      };
    });
  }

  private waitForSharedRefresh(refreshToken: string): Observable<JwTokenModel | null> {
    return new Observable(subscriber => {
      let settled = false;
      const stop = () => {
        settled = true;
        window.removeEventListener('storage', onStorage);
        clearTimeout(timerId);
      };
      const deliver = (result: Observable<JwTokenModel | null>) => {
        if (settled) {
          return;
        }
        stop();
        result.subscribe({
          next: value => subscriber.next(value),
          error: err => subscriber.error(err),
          complete: () => subscriber.complete(),
        });
      };
      const look = (steal: boolean) => {
        const shared = this.rotationObservable(refreshToken);
        if (shared) {
          deliver(shared);
          return;
        }
        if (steal) {
          const lock = this.readRefreshLock();
          if (lock?.refreshToken === refreshToken && lock.owner !== this.refreshOwner) {
            localStorage.removeItem(this.refreshLockKey);
          }
        }
        if (!this.otherTabOwnsRefreshLock(refreshToken)) {
          deliver(this.beginRefresh(refreshToken));
        }
      };
      const onStorage = (event: StorageEvent) => {
        if (event.key === 'tokenInfo' && event.newValue == null && this.tokenInfo?.refresh_token === refreshToken) {
          this.userInfo$.next(null);
          this.myPatients$.next(null);
          this.tokenInfo$.next(null);
          deliver(of(null));
          return;
        }
        if (event.key === 'tokenInfo' || event.key === this.refreshLockKey) {
          look(false);
        }
      };
      window.addEventListener('storage', onStorage);
      const timerId = setTimeout(() => look(true), 8000);
      look(false);
      return () => {
        if (!settled) {
          stop();
        }
      };
    });
  }

  private rotationObservable(refreshToken: string): Observable<JwTokenModel | null> | null {
    const outcome = this.rotationOutcome(refreshToken);
    if (outcome.kind === 'continue') {
      return null;
    }
    if (outcome.kind === 'use') {
      return of(outcome.token);
    }
    return throwError(() => new RefreshSupersededError());
  }

  private rotationOutcome(refreshToken: string):
    { kind: 'continue' } | { kind: 'use'; token: JwTokenModel } | { kind: 'superseded' } {
    if (this.tokenInfo?.refresh_token !== refreshToken) {
      return { kind: 'superseded' };
    }
    const stored = this.readStoredToken();
    if (!stored?.refresh_token || stored.refresh_token === refreshToken) {
      return { kind: 'continue' };
    }
    if (!this.sameSession(this.tokenInfo?.access_token, stored.access_token)) {
      return { kind: 'superseded' };
    }
    this.tokenInfo$.next(stored);
    return { kind: 'use', token: stored };
  }

  private readStoredToken(): JwTokenModel | null {
    try {
      const raw = localStorage.getItem('tokenInfo');
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as JwTokenModel;
      return parsed?.access_token ? parsed : null;
    } catch {
      return null;
    }
  }

  private sameSession(startedToken: string | null | undefined, storedToken: string | null | undefined): boolean {
    const started = this.sessionContext(startedToken);
    const stored = this.sessionContext(storedToken);
    return !!started && !!stored
      && started.userId === stored.userId
      && started.impersonatedUserId === stored.impersonatedUserId
      && started.viewOnly === stored.viewOnly;
  }

  private sessionContext(accessToken: string | null | undefined):
    { userId: string; impersonatedUserId: string; viewOnly: boolean } | null {
    const segment = accessToken?.split('.')[1];
    if (!segment) {
      return null;
    }
    try {
      const padded = segment.replace(/-/g, '+').replace(/_/g, '/');
      const claims = JSON.parse(atob(padded.padEnd(padded.length + (4 - padded.length % 4) % 4, '=')));
      if (claims?.sid == null || claims.sid === '') {
        return null;
      }
      const impersonated = claims.impersonatinguserid == null || claims.impersonatinguserid === ''
        ? ''
        : String(claims.impersonatinguserid);
      return {
        userId: String(claims.sid),
        impersonatedUserId: impersonated,
        viewOnly: String(claims.impersonatingviewonly).toLowerCase() === 'true',
      };
    } catch {
      return null;
    }
  }

  private readRefreshLock(): { refreshToken: string; owner: string } | null {
    try {
      const raw = localStorage.getItem(this.refreshLockKey);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as { refreshToken?: string; owner?: string };
      if (!parsed?.refreshToken || !parsed?.owner) {
        return null;
      }
      return { refreshToken: parsed.refreshToken, owner: parsed.owner };
    } catch {
      return null;
    }
  }

  private otherTabOwnsRefreshLock(refreshToken: string): boolean {
    const lock = this.readRefreshLock();
    return lock?.refreshToken === refreshToken && lock.owner !== this.refreshOwner;
  }

  private writeRefreshLock(refreshToken: string): void {
    try {
      localStorage.setItem(this.refreshLockKey, JSON.stringify({
        refreshToken,
        owner: this.refreshOwner,
      }));
    } catch {
      // Private browsing can reject storage; this tab still refreshes.
    }
  }

  private clearOwnRefreshLock(refreshToken: string | null | undefined): void {
    const lock = this.readRefreshLock();
    if (refreshToken && lock?.refreshToken === refreshToken && lock.owner === this.refreshOwner) {
      try {
        localStorage.removeItem(this.refreshLockKey);
      } catch {
        // Storage can be unavailable in private mode.
      }
    }
  }

  public token() {
    if (this.tokenInfo) {
      return of(this.tokenInfo);
    }
    if (this._gettingTokenInfo)
      return this.tokenInfo$.asObservable();

    return this.getTokenInfo().pipe(
      map((tokenInfo) => {
        if (tokenInfo) {
          return tokenInfo;
        }
        return null;
      }));
  }


  private _gettingUserInfo = false;
  public getUserInfo(): Observable<UserModel | null> {


    this._gettingUserInfo = true;
    return this.accountApiService.Me()

      .pipe(
        tap((result) => {
          this._gettingUserInfo = false;
          this.userInfo$.next(result);
        }),
        catchError((err) => {
          this._gettingUserInfo = false;
          if (this.userInfo && (err?.status === 0 || err?.status === 504)) {
            return of(this.userInfo);
          }
          this.userInfo$.next(null);
          return throwError(() => err);
        }));
  }

  public user() {
    if (this.userInfo) {
      return of(this.userInfo);
    }
    if (this._gettingUserInfo)
      return this.userInfo$.asObservable();

    return this.getUserInfo().pipe(
      map((userInfo) => {
        if (userInfo) {
          return userInfo;
        }
        return null;
      }));
  }

  public getMyPatients() {
    if (this.myPatients) {
      return of(this.myPatients);
    }
    if (this._gettingMyPatients)
      return this.myPatients$.asObservable();

    this._gettingMyPatients = true;
    return this.staffApiService.GetMyPatients()
      .pipe(tap(
        (result) => {
          this._gettingMyPatients = false;
          this.myPatients$.next(result);
        },
        (err) => {
          this._gettingMyPatients = false;
          this.myPatients$.next(null);
          return of(null);
        }
      ));
  }
  private _gettingMyPatients = false;


  private _gettingSettings = false;
  public getSettings(): Observable<GeneralSettingsModel | null> {

    this._gettingSettings = true;
    return this.accountApiService.GeneralSettings()

      .pipe(tap(
        (result) => {
          this._gettingSettings = false;
          this.generalSettings$.next(result);
        },
        (err) => {
          this._gettingSettings = false;
          this.generalSettings$.next(null);
          return of(null);
        }));
  }

  public settings() {
    if (this.generalSettings) {
      return of(this.generalSettings);
    }
    if (this._gettingSettings)
      return this.generalSettings$.asObservable();
    return this.getSettings().pipe(
      map((generalSettings) => {
        if (generalSettings) {
          return generalSettings;
        }
        return null;
      }));
  }



  public getClientFromUrl() {
    const url = window.location.hostname.split('.')[0].toLowerCase();
    if (url.startsWith('spark-'))
      return url.substring(6);
    return url;
  }

  public getOptoURL() {

    var host = window.location.hostname.toLowerCase();

    if (host.startsWith('spark-'))
      return 'https://' + host.substring(6);
    if (host == 'localhost')
      return 'https://localhost:44354/';
    if (environment.production && this.generalSettings?.SiteName)
      return `https://${this.generalSettings?.SiteName}.optomiser.com`;
    return null;
  }


  /*
  public setCookie(cname: string, cvalue: string, exdays: number) {
    var expires = "";
    if (exdays) {
      var d = new Date();
      d.setTime(d.getTime() + (exdays * 24 * 60 * 60 * 1000));
      expires = ";expires=" + d.toUTCString();
    }
    document.cookie = cname + "=" + cvalue + expires + ";path=/";
  }

  public getCookie(cname: string) {
    var name = cname + "=";
    var ca = document.cookie.split(';');
    for (var i = 0; i < ca.length; i++) {
      var c = ca[i];
      while (c.charAt(0) == ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) == 0) {
        return c.substring(name.length, c.length);
      }
    }
    return "";
  }
  */


}
